import React, { Component } from 'react';
import DocumentTitle from 'react-document-title';
import {
  Button,
  Container,
  Dimmer,
  Dropdown,
  Grid,
  Header,
  Icon,
  Label,
  Loader,
  Message,
  Segment
} from 'semantic-grab-ui/src';
import AceEditor from 'react-ace';
import TridentCampaignBuilder from '../common/TridentCampaignBuilder';
import NodePreview from '../show/NodePreview';
import DiffModal from './DiffModal';
import { nodeTypes, startingNodeTypes } from '../../../data/trident-types';
import { defaultOwner, ownerQuest, targetTypeList } from '../../../data/trident-campaigns';
import { INITIAL_NODE } from 'omega-rtc/utils/trident-node-utils';
import _ from 'lodash';
import yaml from 'js-yaml';
import moment from 'moment';
import { connect } from 'react-redux';
import { saveEventConditionActionConfigurations, getECAConfigByKey } from '../../../actions/newTridentExtra';
import Toastr from 'utils/toastr-util';
import { hasJustSuccess, hasJustFail } from 'utils/request-state-utils';

// Import ace editor modes and themes
import 'ace-builds/src-noconflict/mode-yaml';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/ext-searchbox';

// Configure ace to not use workers
import ace from 'ace-builds/src-noconflict/ace';
import './ManageEventConditionActionPage.scss';

import { validateYaml, validateAllConfigs } from './yamlValidators';

// Type definitions
interface UserState {
  name?: string;
  email?: string;
  accessToken?: string;
  refreshToken?: string;
  relogin?: boolean;
  userPermissions?: any;
}

interface EventConditionActionConfig {
  config?: any;
  version?: number;
  createdAt?: string;
  createdBy?: string;
  comment?: string;
}

interface EventConditionActionConfigs {
  [key: string]: EventConditionActionConfig;
}

interface ConfigMetadata {
  version?: number;
  createdAt?: string;
  createdBy?: string;
  comment?: string;
}

interface ConfigKey {
  key: string;
  title: string;
  shortTitle: string;
  description: string;
  icon: string;
  color: string;
  gradient: string;
  category: string;
}

interface CampaignModel {
  nodes: any;
  targetUserType: string;
  owner: string;
  startTime: moment.Moment;
  endTime: moment.Moment;
  nodesOccMap: {
    scenarios: any[];
    time: any[];
    schedule: any[];
    condition: any[];
    count: any[];
    countCondition: any[];
    actions: any[];
    delay: any[];
    randomizer: any[];
    experiment: any[];
    experimentTreatment: any[];
    enumeration: any[];
  };
}

interface Props {
  saveEventConditionActionConfigurations: (actionData: any) => any;
  getECAConfigByKey: (key: string) => any;
  saveConfigurationsRequest?: any;
  user?: UserState;
  eventConditionActionConfigs?: EventConditionActionConfigs;
}

interface State {
  activeTabIndex: number;
  configs: { [key: string]: string };
  originalConfigs: { [key: string]: string };
  configMetadata: { [key: string]: ConfigMetadata | null };
  validationErrors: { [key: string]: string | null };
  crossReferenceErrors: string | null;
  saveStatus: { [key: string]: string | null };
  isLoading: boolean;
  hasUnsavedChanges: boolean;
  editorTheme: string;
  selectedTargetUserType: string;
  selectedOwner: string;
  campaignModel: CampaignModel;
  renderNodePreview: boolean;
  listOfParentNodesKey: any[];
  currentClickedNode: string;
  clickedScenarioIndex: number;
  showModal: { [key: string]: boolean };
  scenarioIndex: number;
  nodeType: string;
  treatmentStartKeysList: any[];
  maxNodeKeyValue: number;
  currentNode: string;
  parentNode: string;
  parentKeyList: any[];
  showCampaignBuilder: boolean;
  showDiffModal: boolean;
}

// Enhanced owner type list for filtering
const OWNER_TYPE_LIST = [
  defaultOwner,   // TARGETING
  ownerQuest,     // quest
];

const CONFIG_KEYS: ConfigKey[] = [
  {
    key: 'eventTypes',
    title: 'Event Types',
    shortTitle: 'Events',
    description: 'Define available event types that can trigger actions',
    icon: 'calendar outline',
    color: 'blue',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    category: 'Core'
  },
  {
    key: 'conditions',
    title: 'Conditions',
    shortTitle: 'Conditions',
    description: 'Configure condition logic for event processing',
    icon: 'filter',
    color: 'green',
    gradient: 'linear-gradient(135deg, #00b09b 0%, #96c93d 100%)',
    category: 'Logic'
  },
  {
    key: 'actions',
    title: 'Actions',
    shortTitle: 'Actions',
    description: 'Define actions that can be executed when conditions are met',
    icon: 'lightning',
    color: 'orange',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    category: 'Core'
  },
  {
    key: 'eventAcl',
    title: 'Event ACL',
    shortTitle: 'Event Permissions',
    description: 'Access control list for event management permissions',
    icon: 'shield alternate',
    color: 'red',
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    category: 'Security'
  },
  {
    key: 'actionAcl',
    title: 'Action ACL',
    shortTitle: 'Action Permissions',
    description: 'Access control list for action execution permissions',
    icon: 'key',
    color: 'purple',
    gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    category: 'Security'
  }
];

export class ManageEventConditionActionPage extends Component<Props, State> {
  private _conditionsCache: any = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      activeTabIndex: 0,
      configs: {},
      originalConfigs: {},
      configMetadata: {}, // Store version, createdAt, createdBy, comment for each config key
      validationErrors: {},
      crossReferenceErrors: null,
      saveStatus: {},
      isLoading: false,
      hasUnsavedChanges: false,
      editorTheme: 'github',
      selectedTargetUserType: 'passenger', // Default to passenger
      selectedOwner: 'TARGETING', // Default to TARGETING
      campaignModel: {
        nodes: {},
        targetUserType: 'passenger',
        owner: 'TARGETING',
        startTime: moment(),
        endTime: moment().add(1, 'day'),
        nodesOccMap: {
          scenarios: [],
          time: [],
          schedule: [],
          condition: [],
          count: [],
          countCondition: [],
          actions: [],
          delay: [],
          randomizer: [],
          experiment: [],
          experimentTreatment: [],
          enumeration: []
        }
      },
      renderNodePreview: false,
      listOfParentNodesKey: [],
      currentClickedNode: '',
      clickedScenarioIndex: -1,
      showModal: {},
      scenarioIndex: 0,
      nodeType: '',
      treatmentStartKeysList: [],
      maxNodeKeyValue: 0,
      currentNode: '',
      parentNode: '',
      parentKeyList: [],
      showCampaignBuilder: false,
      showDiffModal: false
    };
  }

  componentDidMount() {
    // Ensure ace workers are disabled
    if (typeof window !== 'undefined' && (window).ace) {
      (window).ace.config.set('workerPath', false);
      (window).ace.config.set('loadWorkerFromBlob', false);
    }

    this.loadConfigurations();

    // Initialize treatment start keys list
    this.setState({
      treatmentStartKeysList: this.getTreatmentStartKeysList(this.state.campaignModel.nodes)
    });
  }

  loadConfigurations = async () => {
    this.setState({ isLoading: true });

    try {
      // Dispatch requests per config key; reducer will store JSON under state.newTridents.eventConditionActionConfigs
      const keys = CONFIG_KEYS.map(k => k.key);
      await Promise.all(keys.map((key) => this.props.getECAConfigByKey(key)));
    } catch (error) {
      console.error('Failed to load configurations:', error);
      this.setState({ isLoading: false });
    }
  };

  componentDidUpdate(prevProps: Props) {
    // Handle save request state changes
    if (hasJustSuccess(prevProps.saveConfigurationsRequest, this.props.saveConfigurationsRequest)) {
      Toastr.success('Successfully saved configuration(s)');
      // Reload configurations to get updated metadata (version, comment, etc.)
      this.loadConfigurations();
    }

    if (hasJustFail(prevProps.saveConfigurationsRequest, this.props.saveConfigurationsRequest)) {
      this.setState({ isLoading: false });
      Toastr.error('Failed to save configurations. Please try again.');
    }

    const prevConfigs = _.get(prevProps, 'eventConditionActionConfigs');
    const currConfigs = _.get(this.props, 'eventConditionActionConfigs');
    if (prevConfigs !== currConfigs && currConfigs) {
      const keys = CONFIG_KEYS.map(k => k.key);
      const yamlByKey: { [key: string]: string } = {};
      const originalConfigsByKey: { [key: string]: string } = {};
      const metadataByKey: { [key: string]: ConfigMetadata | null } = {};
      keys.forEach((key) => {
        try {
          // Extract config JSON from the EventConditionActionRawConfig response format
          const configResponse = currConfigs[key];
          if (configResponse && configResponse.config) {
            // Convert JSON config to YAML
            const yamlContent = yaml.dump(configResponse.config, { indent: 2, lineWidth: 120 });
            yamlByKey[key] = yamlContent;
            // Store the original backend response for originalConfigs
            originalConfigsByKey[key] = yamlContent;
            
            // Extract metadata
            metadataByKey[key] = {
              version: configResponse.version,
              createdAt: configResponse.createdAt,
              createdBy: configResponse.createdBy,
              comment: configResponse.comment
            };
          } else {
            // If no config, set empty content
            yamlByKey[key] = '';
            originalConfigsByKey[key] = '';
            metadataByKey[key] = null;
          }
        } catch (e) {
          console.error(`Error processing config for ${key}:`, e);
          yamlByKey[key] = '';
          originalConfigsByKey[key] = '';
          metadataByKey[key] = null;
        }
      });
      this.setState({
        configs: yamlByKey,
        originalConfigs: originalConfigsByKey,
        configMetadata: metadataByKey,
        isLoading: false,
      });
    }
  }



  handleConfigChange = (configKey: string, value: string) => {
    const error = validateYaml(value, configKey);

    // Clear cache when configs change to ensure fresh data
    this.clearCache();

    this.setState(prevState => {
      const newConfigs = {
        ...prevState.configs, 
        [configKey]: value
      };

      // Perform cross-reference validation with all configs
      const crossRefErrors = this.performCrossReferenceValidation(newConfigs);
      
      return {
        configs: newConfigs,
        validationErrors: {
          ...prevState.validationErrors,
          [configKey]: error
        },
        crossReferenceErrors: crossRefErrors,
        hasUnsavedChanges: true
      };
    });
  };

  handleEditorChange = (configKey: string, value: string) => {
    this.handleConfigChange(configKey, value);
  };

  // Perform cross-reference validation and return errors
  performCrossReferenceValidation = (configs: { [key: string]: string }) => {
    const validationResults: any = validateAllConfigs(configs);
    
    // Check for cross-reference errors and return them without showing Toastr
    if (validationResults._crossReference) {
      return validationResults._crossReference;
    }

    return null;
  };

  toggleTheme = () => {
    this.setState(prevState => ({
      editorTheme: prevState.editorTheme === 'github' ? 'monokai' : 'github'
    }));
  };

  formatYaml = (configKey: string) => {
    // Basic YAML formatting - can be enhanced with a proper YAML parser
    const value = this.state.configs[configKey];
    if (!value) return;
    try {
      // Simple formatting: ensure proper spacing and indentation
      const lines = value.split('\n');
      const formatted = lines.map(line => {
        const trimmed = line.trim();
        if (trimmed.startsWith('-')) {
          return '  ' + trimmed;
        }
        if (trimmed.includes(':') && !trimmed.startsWith('#')) {
          const [key, ...valueParts] = trimmed.split(':');
          const value = valueParts.join(':').trim();
          return key.trim() + ': ' + value;
        }
        return line;
      }).join('\n');

      this.handleConfigChange(configKey, formatted);
    } catch (error) {
      console.warn('YAML formatting failed:', error);
    }
  };

  handleSaveConfig = async (configKey: string) => {
    const { configs, validationErrors, crossReferenceErrors } = this.state;

    if (validationErrors[configKey]) {
      return;
    }

    // Check for cross-reference validation errors
    if (crossReferenceErrors) {
      return;
    }

    this.setState(prevState => ({
      saveStatus: {
        ...prevState.saveStatus,
        [configKey]: 'saving'
      }
    }));

    try {
      // Simulate API call to save configuration
      await new Promise(resolve => setTimeout(resolve, 1000));

      // In real implementation, replace with actual API call
      // await this.saveConfigurationToAPI(configKey, configs[configKey]);

      this.setState(prevState => ({
        saveStatus: {
          ...prevState.saveStatus,
          [configKey]: 'success'
        },
        originalConfigs: {
          ...prevState.originalConfigs,
          [configKey]: configs[configKey]
        }
      }));

      // Clear success message after 3 seconds
      setTimeout(() => {
        this.setState(prevState => ({
          saveStatus: {
            ...prevState.saveStatus,
            [configKey]: null
          }
        }));
      }, 3000);

    } catch (error) {
      console.error(`Failed to save ${configKey}:`, error);
      this.setState(prevState => ({
        saveStatus: {
          ...prevState.saveStatus,
          [configKey]: 'error'
        }
      }));
    }
  };

  handleResetConfig = (configKey: string) => {
    const { originalConfigs } = this.state;

    this.setState(prevState => ({
      configs: {
        ...prevState.configs,
        [configKey]: originalConfigs[configKey]
      },
      validationErrors: {
        ...prevState.validationErrors,
        [configKey]: null
      }
    }));
  };

  getListItemCount = (yamlContent: string): number => {
    if (!yamlContent) return 0;

    try {
      // Parse the YAML content to get actual data structure
      const cleanContent = yamlContent
        .replace(/^#.*$/gm, '') // Remove comment lines
        .replace(/^(eventTypes|conditions|actions|eventAcl|actionAcl):\s*$/m, '') // Remove top-level keys
        .trim();

      if (!cleanContent) return 0;

      const parsed = yaml.load(cleanContent);

      if (!parsed) return 0;

      // Count based on the data structure type
      if (Array.isArray(parsed)) {
        return parsed.length;
      } else if (typeof parsed === 'object' && parsed !== null) {
        // For objects, count the top-level keys
        return Object.keys(parsed).length;
      }

      return 0;
    } catch (error) {
      // Fallback to original counting method if YAML parsing fails
      console.warn('YAML parsing failed for counting, using fallback method:', error);
      const lines = yamlContent.split('\n');

      return lines.filter(line => {
        const trimmed = line.trim();
        if (!trimmed.startsWith('-') || trimmed.length <= 1) return false;

        // Count leading spaces to determine nesting level
        const leadingSpaces = line.length - line.trimStart().length;

        // Consider items with 2 or fewer leading spaces as parent items
        return leadingSpaces <= 2;
      }).length;
    }
  };

  handleSaveAll = () => {
    const { configs, validationErrors, crossReferenceErrors } = this.state;

    // Check if any configs have validation errors
    const hasErrors = Object.values(validationErrors).some(error => error !== null);
    if (hasErrors) {
      return;
    }

    // Check for cross-reference validation errors
    if (crossReferenceErrors) {
      return;
    }

    // Show diff modal for review before saving
    this.setState({ showDiffModal: true });
  };

  // Method to actually save all configurations after review
  performSaveAll = async () => {
    this.setState({
      showDiffModal: false,
      isLoading: true
    });

    try {
      // Save all configurations
      await Promise.all(
        CONFIG_KEYS.map(config => this.handleSaveConfig(config.key))
      );

      this.setState({
        isLoading: false,
        hasUnsavedChanges: false
      });
    } catch (error) {
      console.error('Failed to save all configurations:', error);
      this.setState({ isLoading: false });
    }
  };

  // Close diff modal without saving
  closeDiffModal = () => {
    this.setState({ showDiffModal: false });
  };

  // Handle saving configurations using Redux action
  handleSaveConfigurations = async (actionData: any) => {
    // Check for cross-reference validation errors
    if (this.state.crossReferenceErrors) {
      Toastr.error('Cross-reference validation failed');
      return Promise.reject(new Error('Cross-reference validation failed'));
    }

    if (!actionData.changedKeys || actionData.changedKeys.length === 0) {
      Toastr.info('No changes to save');
      return;
    }

    this.setState({ isLoading: true });

    // Dispatch the Redux action - success/failure will be handled by componentDidUpdate
    this.props.saveEventConditionActionConfigurations(actionData);
  };

  // Helper method to check if there are any unsaved changes
  hasAnyUnsavedChanges = (configs: { [key: string]: string }, originalConfigs: { [key: string]: string }): boolean => {
    return CONFIG_KEYS.some(config => {
      const configKey = config.key;
      return (configs[configKey] || '') !== (originalConfigs[configKey] || '');
    });
  };

  // Transform YAML configs into listRuleTypes format for TridentCampaignBuilder
  transformConfigsToListRuleTypes = () => {
    const { configs, selectedTargetUserType, selectedOwner } = this.state;
    const listRuleTypes: any = {};

    try {
      // Parse the YAML configurations using fast js-yaml library
      const eventTypesContent = configs.eventTypes || '';
      const actionsContent = configs.actions || '';
      const eventAclContent = configs.eventAcl || '';
      const actionAclContent = configs.actionAcl || '';

      // Parse all configurations in parallel using js-yaml (much faster than custom parser)
      const [actionsMap, eventAclMap, actionAclMap, eventTypesMap] = [
        actionsContent ? yaml.load(actionsContent.replace(/^# Actions Configuration\s*/, '').replace(/^actions:\s*/, '')) : {},
        eventAclContent ? yaml.load(eventAclContent.replace(/^# Event ACL Configuration\s*/, '')) : {},
        actionAclContent ? yaml.load(actionAclContent.replace(/^# Action ACL Configuration\s*/, '')) : {},
        eventTypesContent ? yaml.load(eventTypesContent.replace(/^# Event Types Configuration\s*/, '')) : {}
      ];

      // Pre-compute rule types for all variables to avoid repeated transformations
      const ruleTypeCache = new Map();
      const getRuleType = (variableName: string) => {
        if (!ruleTypeCache.has(variableName)) {
          ruleTypeCache.set(variableName, this.transformRuleTypeVariable(variableName));
        }
        return ruleTypeCache.get(variableName);
      };

      // Group events by user type from eventAcl using optimized loops
      const eventKeys = Object.keys(eventTypesMap);
      for (let i = 0; i < eventKeys.length; i++) {
        const eventKey = eventKeys[i];
        const eventConfig = eventTypesMap[eventKey];
        const eventAcl = eventAclMap[eventKey];

        if (!eventAcl || !eventAcl.targetUserTypes) {
          continue;
        }

        // Pre-compute allowed actions for this event to avoid repeated calculations
        const eventActions = eventConfig.actions || [];
        const preComputedActions = new Map();

        // Filter for only the selected target user type and owner
        const targetUserTypes = eventAcl.targetUserTypes;
        const owners = eventAcl.owners || [];

        if (!targetUserTypes.includes(selectedTargetUserType) || !owners.includes(selectedOwner)) {
          continue; // Skip this event if it doesn't support the selected user type or owner
        }

        const userType = selectedTargetUserType;
        if (!listRuleTypes[userType]) {
          listRuleTypes[userType] = [];
        }

        // Get or compute allowed actions for this user type
        if (!preComputedActions.has(userType)) {
          const allowedActions = [];
          for (let k = 0; k < eventActions.length; k++) {
            const actionKey = eventActions[k];
            const actionConfig = actionsMap[actionKey];
            const actionAcl = actionAclMap[actionKey];

            // Check if this user type and owner can use this action
            const actionOwners = actionAcl?.owners || [];
            if (actionConfig &&
              actionAcl?.targetUserTypes?.includes(userType) &&
              actionOwners.includes(selectedOwner)) {
              allowedActions.push({
                type: actionKey,
                noun: actionConfig.name || actionKey,
                group: actionConfig.group || 'others',
                description: actionConfig.description || '',
                autoGenerateFormEnabled: actionConfig.autoGenerateFormEnabled || false
              });
            }
          }
          preComputedActions.set(userType, allowedActions);
        }

        // Build rule types using cached transformation
        const ruleTypes = eventConfig.ruleTypes
          ? eventConfig.ruleTypes.map(getRuleType)
          : [];

        // Create the event object
        listRuleTypes[userType].push({
          allowedActions: preComputedActions.get(userType),
          description: eventConfig.description || '',
          eventType: eventKey,
          group: eventConfig.group || 'Others',
          limit: {
            scenario: [4, 4, 4, 4], // Default limits - placeholder
            action: [4, 4, 4, 4]    // Default limits - placeholder
          },
          limitVars: eventConfig.limitVars || [],
          noun: eventConfig.name || eventKey,
          ruleTypes: ruleTypes
        });
      }

    } catch (error) {
      console.warn('Error transforming configs to listRuleTypes:', error);
    }

    return listRuleTypes;
  };

  // Get parsed conditions data with caching
  getConditionsData = () => {
    if (!this._conditionsCache) {
      try {
        const conditionsContent = this.state.configs.conditions || '';
        if (conditionsContent && conditionsContent.trim()) {
          this._conditionsCache = yaml.load(conditionsContent);
        } else {
          this._conditionsCache = {};
        }
      } catch (error) {
        console.warn('Error parsing conditions YAML:', error);
        this._conditionsCache = {};
      }
    }
    return this._conditionsCache;
  };

  // Clear cache when configs change
  clearCache = () => {
    this._conditionsCache = null;
  };

  // Helper method to transform rule type variables into rule type objects
  transformRuleTypeVariable = (variableName: string) => {
    const conditionsData = this.getConditionsData();

    // Check if variable exists in conditions configuration
    if (conditionsData && conditionsData[variableName]) {
      const config = conditionsData[variableName];

      // Convert comma-separated allowedOperators string to array
      const allowedOperators = config.allowedOperators
        ? config.allowedOperators.split(',').map((op: string) => op.trim())
        : ['equals', 'not_equals'];

      return {
        description: config.description || `Rule based on ${variableName}`,
        group: config.group || 'OTHER',
        variableName: variableName,
        noun: config.noun || variableName.split('.').pop(),
        allowedOperators: allowedOperators,
        objectType: config.objectType || 'string',
        ...(config.options && { options: config.options })
      };
    }

    // Create default rule type for unmapped variables
    const parts = variableName.split('.');
    const lastPart = parts[parts.length - 1];
    const category = parts[1] || 'OTHER';

    return {
      description: `Rule based on ${variableName}`,
      group: category.toUpperCase(),
      variableName: variableName,
      noun: lastPart.charAt(0).toUpperCase() + lastPart.slice(1),
      allowedOperators: ['equals', 'not_equals'],
      objectType: 'string'
    };
  };

  // Handle campaign model changes
  handleCampaignModelChange = (updatedModel: CampaignModel) => {
    this.setState({
      campaignModel: updatedModel || this.state.campaignModel
    });
  };

  // Set list of ancestors of current clicked node and render node preview.
  handleNodePreview = (listOfParentNodesKey: any[], currentClickedNode: string, clickedScenarioIndex: number = -1) => {
    this.setState(
      {
        renderNodePreview: true,
        listOfParentNodesKey,
        currentClickedNode,
        clickedScenarioIndex,
      }
    );
  };

  // Code to close node preview.
  closeNodePreview = () => {
    this.setState(
      {
        currentClickedNode: '',
        listOfParentNodesKey: [],
        renderNodePreview: false,
      }
    );
  };

  // Helper method to get treatment start keys list
  getTreatmentStartKeysList = (nodes: any) => {
    const nodeKeys = _.keysIn(nodes);
    const treatmentStartKeysList: any[] = [];
    nodeKeys.forEach(nodeKey => {
      if (startingNodeTypes.includes(nodes[nodeKey].type)) {
        treatmentStartKeysList.push(nodeKey);
      }
    });
    return treatmentStartKeysList;
  };

  // Helper method to get max node key value
  getMaxNodeKeyValue = (nodes: any): number => {
    const nodeKeys = _.keysIn(nodes);
    let maxValue = 0;
    nodeKeys.forEach(nodeKey => {
      const keyValue = parseInt(nodeKey, 10);
      if (!isNaN(keyValue) && keyValue > maxValue) {
        maxValue = keyValue;
      }
    });
    return maxValue;
  };

  // Add node to occurrence map
  addNodeToOccMap = (nodesOccMap: any, nodeID: string, nodeType: string) => {
    if (!nodesOccMap[nodeType]) {
      nodesOccMap[nodeType] = [];
    }
    nodesOccMap[nodeType].push({
      nodeID,
      key: (nodesOccMap[nodeType].length) + 1,
    });
  };

  // Add node implementation
  addNode = (nodeType: string, parentKey: string = '-1', parentKeyList: any[] = [], defaultData: any = null, remainInModal: boolean = false, nodeKVMap: any = null) => {
    const updatedModel = _.cloneDeep(this.state.campaignModel);

    // Create new node
    let maxNodeKeyValue = this.state.maxNodeKeyValue;
    maxNodeKeyValue += 1;
    const newNodeID = maxNodeKeyValue.toString();
    const newNode = _.cloneDeep(INITIAL_NODE[nodeType]);

    if (defaultData) {
      newNode.data = defaultData;
    }

    if (nodeKVMap) {
      for (const [key, value] of Object.entries(nodeKVMap)) {
        newNode[key] = value;
      }
    }

    _.set(updatedModel.nodes, newNodeID, newNode);

    // Add the new node to the parent node's children list
    if (parentKey !== '-1') {
      const childrenList = _.get(updatedModel.nodes[parentKey], 'children', []);
      childrenList.push(newNodeID);
      _.set(updatedModel.nodes[parentKey], 'children', childrenList);
    }

    // Add the new node to the occurrence map
    const nodesOccMap = _.get(updatedModel, 'nodesOccMap');
    this.addNodeToOccMap(nodesOccMap, newNodeID, nodeType);
    _.set(updatedModel, 'nodesOccMap', nodesOccMap);

    // Update campaign model
    this.handleCampaignModelChange(updatedModel);

    // Update state
    const newParentKeyList = [...parentKeyList, newNodeID];
    this.setState({
      maxNodeKeyValue,
      parentNode: parentKey,
      parentKeyList: newParentKeyList,
      treatmentStartKeysList: this.getTreatmentStartKeysList(updatedModel.nodes)
    });

    if (nodeType !== nodeTypes.experimentTreatment && !remainInModal) {
      this.setState({
        currentNode: newNodeID,
        showModal: {
          builder: true
        },
        nodeType,
      });
    }

    console.log(`Added ${nodeType} node with ID: ${newNodeID}`);
  };

  addDefaultExperiment = () => {
    console.log('addDefaultExperiment is not available in this page');
    // Implement if needed
  };

  // duplicateBranch clones the branch starting with the specified node
  duplicateBranch = (nodeKey: string) => {
    console.log(`duplicate branch is not available on this page`);
  };

  handleDeleteNode = (nodeId: string) => {
    console.log('delete node is not available on this page');
    // Implement node deletion logic if needed
  };

  openModal = (nodeType: string, scenarioIndex: number, type: string) => {
    this.setState({
      showModal: { [type]: true },
      nodeType,
      scenarioIndex,
    });
  };

  closeModal = () => {
    this.setState({
      showModal: {},
      currentNode: '',
      nodeType: ''
    });
  };

  // Reset campaign builder state to initial values
  resetCampaignBuilderState = () => {
    return {
      campaignModel: {
        nodes: {},
        targetUserType: 'passenger',
        owner: 'TARGETING',
        startTime: moment(),
        endTime: moment().add(1, 'day'),
        nodesOccMap: {
          scenarios: [],
          time: [],
          schedule: [],
          condition: [],
          count: [],
          countCondition: [],
          actions: [],
          delay: [],
          randomizer: [],
          experiment: [],
          experimentTreatment: [],
          enumeration: []
        }
      },
      renderNodePreview: false,
      listOfParentNodesKey: [],
      currentClickedNode: '',
      clickedScenarioIndex: -1,
      showModal: {},
      scenarioIndex: 0,
      nodeType: '',
      treatmentStartKeysList: [],
      maxNodeKeyValue: 0,
      currentNode: '',
      parentNode: '',
      parentKeyList: [],
      showCampaignBuilder: false,
      showDiffModal: false
    };
  };

  handleTargetUserTypeChange = (event: any, { value }: { value: string }) => {
    this.setState({
      selectedTargetUserType: value,
      ...this.resetCampaignBuilderState()
    });
  };

  handleOwnerChange = (event: any, { value }: { value: string }) => {
    this.setState({
      selectedOwner: value,
      ...this.resetCampaignBuilderState()
    });
  };

  handlePreviewConfig = (configKey: string) => {
    console.log(`Previewing ${configKey} configuration for target user type: ${this.state.selectedTargetUserType}, owner: ${this.state.selectedOwner}`);

    // Update the campaign model with the selected target user type and owner
    this.setState(prevState => ({
      showCampaignBuilder: true,
      campaignModel: {
        ...prevState.campaignModel,
        targetUserType: prevState.selectedTargetUserType,
        owner: prevState.selectedOwner
      }
    }), () => {
      // Scroll to bottom of the page after the section is rendered
      setTimeout(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
    });
  };

  renderConfigMetadata = (configKey: string) => {
    const { configMetadata } = this.state;
    const metadata = configMetadata[configKey];
    
    if (!metadata) {
      return null;
    }

    const formatDate = (dateString?: string) => {
      if (!dateString) return 'N/A';
      return moment(dateString).format('MMM DD, h:mm A');
    };

    const formatUser = (email?: string) => {
      if (!email) return 'N/A';
      // Extract name from email (e.g., kevin.hutama@grabtaxi.com -> Kevin Hutama)
      const name = email.split('@')[0];
      const formattedName = name.split('.').map(part => 
        part.charAt(0).toUpperCase() + part.slice(1)
      ).join(' ');
      return formattedName;
    };

    return (
      <div className="config-metadata-compact">
        <span className="metadata-item version-item">
          <Icon name="tag" size="small" color="blue" />
          <span className="version-badge">v{metadata.version}</span>
        </span>
        <span className="metadata-item date-item">
          <Icon name="clock" size="small" color="green" />
          <span className="metadata-label">Last Updated:</span>
          {formatDate(metadata.createdAt)}
        </span>
        <span className="metadata-item user-item">
          <Icon name="user" size="small" color="purple" />
          {formatUser(metadata.createdBy)}
        </span>
        {metadata.comment && (
          <span className="metadata-item comment-item">
            <Icon name="comment" size="small" color="orange" />
            "{metadata.comment}"
          </span>
        )}
      </div>
    );
  };

  renderConfigEditor = (config: ConfigKey) => {
    const { configs, validationErrors, crossReferenceErrors, saveStatus } = this.state;
    const configValue = configs[config.key];
    const error = validationErrors[config.key];
    const status = saveStatus[config.key];
    const hasChanges = configValue !== this.state.originalConfigs[config.key];

    return (
      <Segment key={config.key} className="config-editor-segment">
        <Grid>
          <Grid.Row>
            <Grid.Column width={12}>
              <Header as="h3">
                <Icon name={config.icon} color={config.color}/>
                <Header.Content>
                  {config.title}
                  {hasChanges && <Label color="orange" size="mini" style={{ marginLeft: '10px' }}>Modified</Label>}
                  <Header.Subheader>{config.description}</Header.Subheader>
                </Header.Content>
              </Header>
              {/* Configuration Metadata - moved to top */}
              {this.renderConfigMetadata(config.key)}
            </Grid.Column>
            <Grid.Column width={4} textAlign="right">
              <Button
                icon="undo"
                content="Reset"
                size="small"
                disabled={!hasChanges}
                onClick={() => this.handleResetConfig(config.key)}
              />
            </Grid.Column>
          </Grid.Row>

          {/* YAML Validation Error Message */}
          {error && (
            <Grid.Row>
              <Grid.Column width={16}>
                <Message negative size="small">
                  <Icon name="exclamation triangle"/>
                  {error}
                </Message>
              </Grid.Column>
            </Grid.Row>
          )}

          {/* Cross-Reference Validation Error Message */}
          {crossReferenceErrors && (
            <Grid.Row>
              <Grid.Column width={16}>
                <Message negative size="small">
                  <Icon name="warning sign"/>
                  <strong>Cross-reference validation errors:</strong>
                  <div style={{ marginTop: '8px' }}>
                    {crossReferenceErrors.split('; ').map((errorMsg, index) => (
                      <div key={index} style={{ marginBottom: '4px' }}>
                        • {errorMsg}
                      </div>
                    ))}
                  </div>
                </Message>
              </Grid.Column>
            </Grid.Row>
          )}

          <Grid.Row>
            <Grid.Column width={16}>
              <div className="yaml-editor-wrapper">
                <div className={`yaml-editor-toolbar ${this.state.editorTheme === 'monokai' ? 'dark-theme' : ''}`}>
                  <div className="editor-info">
                     <span className="line-count">
                       {configValue ? configValue.split('\n').length : 0} lines
                     </span>
                    <span className="char-count">
                       {configValue ? configValue.length : 0} characters
                     </span>
                  </div>
                  <div className="editor-actions">
                    <button
                      className="format-btn"
                      type="button"
                      title="Toggle Dark/Light Theme"
                      onClick={this.toggleTheme}
                    >
                      <Icon name={this.state.editorTheme === 'github' ? 'moon' : 'sun'}/>
                      {this.state.editorTheme === 'github' ? 'Dark' : 'Light'}
                    </button>
                    <button
                      className="format-btn"
                      type="button"
                      title="Format YAML"
                      onClick={() => this.formatYaml(config.key)}
                    >
                      <Icon name="code"/> Format
                    </button>
                  </div>
                </div>
                <div style={{ position: 'relative' }}>
                  <AceEditor
                    mode="yaml"
                    theme={this.state.editorTheme}
                    value={configValue || ''}
                    onChange={(value) => this.handleEditorChange(config.key, value)}
                    name={`yaml-editor-${config.key}`}
                    editorProps={{
                      $blockScrolling: Infinity,
                      $useWorker: false
                    }}
                    enableBasicAutocompletion={false}
                    enableLiveAutocompletion={false}
                    setOptions={{
                      enableBasicAutocompletion: false,
                      enableLiveAutocompletion: false,
                      enableSnippets: false,
                      showLineNumbers: true,
                      tabSize: 2,
                      useSoftTabs: true,
                      wrap: false,
                      fontSize: 14,
                      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                      showPrintMargin: true,
                      printMargin: 80,
                      showGutter: true,
                      highlightActiveLine: true,
                      highlightSelectedWord: true,
                      cursorStyle: 'ace',
                      mergeUndoDeltas: true,
                      behavioursEnabled: true,
                      wrapBehavioursEnabled: true,
                      autoScrollEditorIntoView: true,
                      copyWithEmptySelection: false,
                      scrollPastEnd: 0.5,
                      useWorker: false
                    }}
                    style={{
                      width: '100%',
                      height: '600px',
                      border: error ? '2px solid #d73a49' : '1px solid #d1d5da',
                      borderRadius: '0 0 6px 6px',
                      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace'
                    }}
                    commands={[
                      {
                        name: 'save',
                        bindKey: { win: 'Ctrl-S', mac: 'Command-S' },
                        exec: () => this.handleSaveConfig(config.key)
                      }
                    ]}
                    onLoad={(editor) => {
                      // Additional worker disabling on editor load
                      try {
                        editor.session.setUseWorker(false);
                      } catch (e) {
                        console.warn('Could not disable worker:', e);
                      }
                    }}
                  />
                </div>
              </div>

              <div className="status-messages">
                {status === 'success' && (
                  <Message positive size="small" style={{ marginTop: '8px' }}>
                    <Icon name="check"/>
                    Configuration saved successfully
                  </Message>
                )}

                {status === 'error' && (
                  <Message negative size="small" style={{ marginTop: '8px' }}>
                    <Icon name="times"/>
                    Failed to save configuration
                  </Message>
                )}
              </div>
            </Grid.Column>
          </Grid.Row>

          {/* Preview Configuration Selection */}
          <Grid.Row>
            <Grid.Column width={16}>
              <div style={{
                marginTop: '1.5rem',
                marginBottom: '1rem',
                padding: '1.5rem',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                borderRadius: '8px',
                border: '1px solid #d1d5da',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
              }}>
                <Header as="h4" style={{
                  marginBottom: '1.5rem',
                  textAlign: 'center',
                  color: '#24292e',
                  fontWeight: '600'
                }}>
                  <Icon name="settings" style={{ marginRight: '8px' }}/>
                  Preview Configuration
                  <Header.Subheader style={{ marginTop: '0.5rem', color: '#6c757d' }}>
                    Select target user type and owner to customize the preview
                  </Header.Subheader>
                </Header>

                <Grid stackable>
                  <Grid.Row>
                    <Grid.Column width={8}>
                      <div style={{ textAlign: 'center' }}>
                        <Label
                          style={{
                            display: 'block',
                            marginBottom: '0.75rem',
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            color: '#586069'
                          }}
                        >
                          <Icon name="users" style={{ marginRight: '6px' }}/>
                          Target User Type
                        </Label>
                        <Dropdown
                          selection
                          fluid
                          value={this.state.selectedTargetUserType}
                          options={targetTypeList}
                          onChange={this.handleTargetUserTypeChange}
                          style={{
                            borderRadius: '6px',
                            border: '1px solid #d1d5da',
                            fontSize: '0.95rem'
                          }}
                          className="elegant-dropdown"
                        />
                      </div>
                    </Grid.Column>

                    <Grid.Column width={8}>
                      <div style={{ textAlign: 'center' }}>
                        <Label
                          style={{
                            display: 'block',
                            marginBottom: '0.75rem',
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            color: '#586069'
                          }}
                        >
                          <Icon name="shield alternate" style={{ marginRight: '6px' }}/>
                          Owner
                        </Label>
                        <Dropdown
                          selection
                          fluid
                          value={this.state.selectedOwner}
                          options={OWNER_TYPE_LIST}
                          onChange={this.handleOwnerChange}
                          style={{
                            borderRadius: '6px',
                            border: '1px solid #d1d5da',
                            fontSize: '0.95rem'
                          }}
                          className="elegant-dropdown"
                        />
                      </div>
                    </Grid.Column>
                  </Grid.Row>
                  {/* Preview Button Row */}
                  <Grid.Row>
                    <Grid.Column width={16} textAlign="center">
                      <Button
                        icon="eye"
                        content="Preview"
                        primary
                        size="medium"
                        disabled={!!error}
                        onClick={() => this.handlePreviewConfig(config.key)}
                        style={{ marginBottom: '0.5rem' }}
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </div>
            </Grid.Column>
          </Grid.Row>

        </Grid>
      </Segment>
    );
  };

  // Get current environment
  getEnvironment = (): string => {
    return _.get(window, 'Grab.config.ENV', 'stg').toUpperCase();
  };

  // Check if current environment is PRD
  isPrdEnvironment = (): boolean => {
    return _.get(window, 'Grab.config.ENV', 'stg') === 'prd';
  };

  render() {
    const { activeTabIndex, isLoading, hasUnsavedChanges, validationErrors, crossReferenceErrors } = this.state;
    const hasErrors = Object.values(validationErrors).some(error => error !== null) || crossReferenceErrors !== null;
    const activeKey = CONFIG_KEYS[activeTabIndex]?.key || CONFIG_KEYS[0].key;
    const environment = this.getEnvironment();
    const isPrd = this.isPrdEnvironment();

    return (
      <DocumentTitle title="Manage Event Condition Action">
        <div className="entity-index-page manage-event-condition-action-page">
          <Dimmer active={isLoading} inverted>
            <Loader>Loading configurations...</Loader>
          </Dimmer>

          <Container fluid className="index-header">
            <Grid>
              <Grid.Row verticalAlign="middle">
                <Grid.Column width={12}>
                  <Header as="h2">
                    <Icon name="settings"/>
                    <Header.Content>
                      Manage Event Condition Action Configurations
                      <Label
                        color={isPrd ? 'red' : 'blue'}
                        size="small"
                        style={{ marginLeft: '10px', verticalAlign: 'middle' }}
                      >
                        {environment}
                      </Label>
                      <Header.Subheader>
                        Configure YAML-based settings for event processing, conditions, actions, and access controls
                      </Header.Subheader>
                    </Header.Content>
                  </Header>
                </Grid.Column>
                <Grid.Column width={4} textAlign="right">
                  <Button
                    icon="save"
                    content="Save All Changes"
                    primary
                    size="large"
                    disabled={!hasUnsavedChanges || hasErrors}
                    onClick={this.handleSaveAll}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Container>

          <Container fluid style={{ padding: '0 1rem' }}>
            {hasUnsavedChanges && (
              <Message warning style={{ marginBottom: '1rem' }}>
                <Icon name="warning sign"/>
                You have unsaved changes. Make sure to save your configurations before leaving this page.
              </Message>
            )}

            <div className="simple-tabs-container">
              <div className="config-tabs">
                {CONFIG_KEYS.map((config, index) => {
                  const isActive = index === activeTabIndex;
                  const hasChanges = this.state.configs[config.key] !== this.state.originalConfigs[config.key];
                  const listItemCount = this.getListItemCount(this.state.configs[config.key]);

                  return (
                    <Button
                      key={config.key}
                      className={`config-tab ${isActive ? 'active' : ''}`}
                      type="button"
                      onClick={() => this.setState({ activeTabIndex: index })}
                      style={{
                        backgroundColor: isActive ? config.color : 'white',
                        color: isActive ? 'white' : config.color,
                        border: `2px solid ${config.color}`,
                        marginRight: '0.5rem'
                      }}
                    >
                      <Icon name={config.icon}/>
                      {config.shortTitle}
                      <span className="list-count">
                                 ({listItemCount})
                               </span>
                      {hasChanges && <Icon name="circle" size="tiny"
                                           style={{ marginLeft: '0.5rem', color: isActive ? 'white' : '#ff6b35' }}/>}
                    </Button>
                  );
                })}
              </div>

              <div className="active-config-editor">
                {this.renderConfigEditor(CONFIG_KEYS[activeTabIndex])}
              </div>
            </div>

            {/* TridentCampaignBuilder Section */}
            {this.state.showCampaignBuilder && (
              <div className="campaign-builder-section"
                   style={{ marginTop: '2rem', padding: '1rem', borderTop: '1px solid #e2e8f0', minHeight: '600px' }}>
                <Header as="h3" style={{ marginBottom: '1rem' }}>
                  <Icon name="sitemap"/>
                  <Header.Content>
                    Campaign Builder Visualizer
                    <Header.Subheader>
                      Preview the scenario, condition and action list based on the configured event types and rules
                    </Header.Subheader>
                  </Header.Content>
                </Header>

                {this.state.renderNodePreview &&
                  <div className="nodePreviewStyle">
                    <div className="nodeStyleCancel">
                      <Icon name="cancel" style={{ fontSize: '1.5em', cursor: 'pointer' }}
                            onClick={this.closeNodePreview}/>
                    </div>
                    <NodePreview
                      onModelChange={this.handleCampaignModelChange}
                      model={this.state.campaignModel}
                      cities={[]}
                      country={{ code: 'SG', name: 'Singapore' }}
                      canActivate={true}
                      handleNodePreview={this.handleNodePreview}
                      currentKey={this.state.currentClickedNode}
                      parentKeyList={this.state.listOfParentNodesKey}
                      clickedScenarioIndex={this.state.clickedScenarioIndex}
                      handleDeleteNode={this.handleDeleteNode}
                      handleAddNode={this.addNode}
                      handleDuplicateBranch={this.duplicateBranch}
                      listRuleTypes={this.transformConfigsToListRuleTypes()}
                      closeModal={this.closeModal}
                      showModal={this.state.showModal.preview}
                      nodeType={this.state.nodeType}
                      openModal={this.openModal}
                      scenarioIndex={this.state.scenarioIndex}
                      addDefaultExperiment={this.addDefaultExperiment}
                    />
                  </div>
                }

                <TridentCampaignBuilder
                  model={this.state.campaignModel}
                  canActivate={true}
                  onModelChange={this.handleCampaignModelChange}
                  cities={[]}
                  country={{ code: 'SG', name: 'Singapore' }}
                  handleNodePreview={this.handleNodePreview}
                  clickedNode={this.state.currentClickedNode}
                  handleDeleteNode={this.handleDeleteNode}
                  addDefaultExperiment={this.addDefaultExperiment}
                  userPermissions={{}}
                  listRuleTypes={this.transformConfigsToListRuleTypes()}
                  treatmentStartKeysList={this.state.treatmentStartKeysList}
                  addNode={this.addNode}
                  closeModal={this.closeModal}
                  showModal={this.state.showModal.builder}
                  renderNodePreview={this.state.renderNodePreview}
                  nodeType={this.state.nodeType}
                  currentNode={this.state.currentNode}
                  parentNode={this.state.parentNode}
                  parentKeyList={this.state.parentKeyList}
                  scenarioIndex={this.state.scenarioIndex}
                  appConfig={{
                    tridentScheduleNodeEnabled: true,
                    tridentTimeNodeDisabled: false
                  }}
                />
              </div>
            )}
          </Container>

          {/* Diff comparison modal */}
          <DiffModal
            isOpen={this.state.showDiffModal}
            onClose={this.closeDiffModal}
            onConfirm={this.performSaveAll}
            onSaveConfigurations={this.handleSaveConfigurations}
            configs={this.state.configs}
            originalConfigs={this.state.originalConfigs}
            configKeys={CONFIG_KEYS}
            user={this.props.user}
            eventConditionActionConfigs={this.props.eventConditionActionConfigs}
          />
        </div>
      </DocumentTitle>
    );
  }
}

// Redux connection  
const mapStateToProps = (state: any) => ({
  // Add any state mappings if needed in the future
  saveConfigurationsRequest: _.get(state.requests, 'saveEventConditionActionConfigurationsRequest', {}),
  user: state.app.user, // Add user to access email for backend requests (UserState type)
  eventConditionActionConfigs: _.get(state, 'newTridents.eventConditionActionConfigs', {}),
});

const mapDispatchToProps = {
  saveEventConditionActionConfigurations,
  getECAConfigByKey,
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageEventConditionActionPage);
